import React from 'react';
import { Typography, Progress, Button } from 'antd';
import { Loader2, X as XIcon, Info, RefreshCw } from 'lucide-react';
import { useAtomValue } from 'jotai';
import {
  dadanExportStatusAtom,
  dadanExportProgressAtom,
  dadanExportProgressMessageAtom,
  DadanExportStatus,
} from '../../atoms/exportAtoms';
import './export.scss';

const { Text, Title } = Typography;

interface ExportProgressProps {
  onRetry?: () => void;
  isRetrying?: boolean;
}

const getStepInfo = (status: DadanExportStatus) => {
  switch (status) {
    case 'preparing':
      return {
        title: 'Preparing your video...',
        subtitle: 'Please don\'t close this page',
      };
    case 'queued':
      return {
        title: 'Queued for processing...',
        subtitle: 'This may take a few minutes',
      };
    case 'stitching':
      return {
        title: 'Combining scenes...',
        subtitle: 'This may take a few minutes',
      };
    case 'uploading':
      return {
        title: 'Uploading to Personal Library...',
        subtitle: 'Almost there!',
      };
    default:
      return {
        title: 'Processing...',
        subtitle: '',
      };
  }
};

const ExportProgress: React.FC<ExportProgressProps> = ({ onRetry, isRetrying }) => {
  const status = useAtomValue(dadanExportStatusAtom);
  const progress = useAtomValue(dadanExportProgressAtom);
  const message = useAtomValue(dadanExportProgressMessageAtom);

  const isFailed = status === 'failed';

  if (isFailed) {
    return (
      <div className="export-failed">
        <div className="export-failed__header">
          <div className="export-failed__icon-wrapper">
            <XIcon size={28} strokeWidth={3} />
          </div>
          <Title level={4} className="export-failed__title">
            Export Failed
          </Title>
          <Text type="secondary" className="export-failed__subtitle">
            Something went wrong while exporting your video
          </Text>
        </div>

        {onRetry && (
          <div className="export-failed__actions">
            <Button
              type="primary"
              size="large"
              icon={<RefreshCw size={16} />}
              onClick={onRetry}
              loading={isRetrying}
              className="export-failed__retry-btn"
              block
            >
              Try Again
            </Button>
          </div>
        )}
      </div>
    );
  }

  const stepInfo = getStepInfo(status);

  return (
    <div className="export-progress">
      <div className="export-progress__header">
        <Title level={5} className="export-progress__title">
          Exporting...
        </Title>
      </div>

      <div className="export-progress__content">
        <div className="export-progress__step">
          <Loader2 className="export-progress__icon export-progress__icon--spinning" size={20} />
          <div className="export-progress__step-text">
            <Text strong>{stepInfo.title}</Text>
            {message && <Text type="secondary">{message}</Text>}
          </div>
        </div>

        <Progress
          percent={progress}
          status="active"
          strokeColor="#4A3CFA"
          className="export-progress__bar"
        />

        <Text type="secondary" className="export-progress__subtitle">
          {stepInfo.subtitle}
        </Text>

        {status !== 'preparing' && (
          <div className="export-progress__background-info">
            <Info size={14} />
            <Text type="secondary">
              Export continues in the background. You can leave this page and we'll notify you when it's ready.
            </Text>
          </div>
        )}
      </div>
    </div>
  );
};

export default ExportProgress;
